/**
 * Blob URLs must outlive the save dialog. With "ask where to save each file"
 * enabled the browser only reads the anchor's href once the user has picked a
 * location, so revoking synchronously leaves a ghost entry in Downloads that
 * never finishes.
 */
const BLOB_URL_TTL_MS = 60_000;

/** Hands a blob to the browser's download machinery under `fileName`. */
export const saveBlob = (blob: Blob, fileName: string): void => {
	const blobUrl = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = blobUrl;
	a.download = fileName;
	a.click();
	setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_TTL_MS);
};

/**
 * Fetches a URL as a blob, reporting cumulative received bytes as they arrive.
 *
 * Progress comes from the body reader rather than `Content-Length`, which is
 * not CORS-exposed on the signed R2 URLs the attachment endpoint redirects to —
 * callers supply their own total from attachment metadata.
 */
export const fetchWithProgress = async (
	url: string,
	onProgress?: (receivedBytes: number) => void,
): Promise<Blob> => {
	const res = await fetch(url);
	if (!res.ok) throw new Error(`Download failed with status ${res.status}`);

	const type = res.headers.get('content-type') ?? '';
	if (!res.body) return res.blob();

	const reader = res.body.getReader();
	const chunks: BlobPart[] = [];
	let received = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		received += value.byteLength;
		onProgress?.(received);
	}
	return new Blob(chunks, { type });
};

/**
 * Downloads a file by fetching its URL and triggering a browser save dialog.
 *
 * This fetches the resource as a blob so the browser treats it as a download
 * rather than navigating to the URL (which may just open in a new tab for
 * images, PDFs, etc.).
 */
export const downloadFile = async (url: string, fileName: string): Promise<void> => {
	const blob = await fetchWithProgress(url);
	saveBlob(blob, fileName);
};
